import "./assets/main.css"
// import "./output.css"

import { createApp } from "vue"
import { createPinia } from "pinia"

import App from "./App.vue"
import router from "./router"

import * as Sentry from "@sentry/vue"

import PrimeVue from "primevue/config"
// -compat presets keep PrimeVue 4's rem sizing. PrimeVue 5 recalibrated the
// default presets for a 16px root font size; ours is already 16px, so the
// non-compat presets would shrink every component by ~12.5%.
import Lara from "@primeuix/themes/lara-compat"

import { PRIMEVUE_LICENSE } from "./primevueLicense"

import Tooltip from "primevue/tooltip"
import FocusTrap from "primevue/focustrap"
import KeyFilter from "primevue/keyfilter"

// import ToastService from "primevue/toastservice"
// import DialogService from "primevue/dialogservice"

import { FontAwesomeIcon, FontAwesomeLayers, FontAwesomeLayersText } from "@fortawesome/vue-fontawesome"
import "./fontAwesomeIcons"

import { initColorScheme } from "@/compositions/colorScheme"

// Apply the stored color scheme (light/dark/auto) before the app renders.
initColorScheme()

const app = createApp(App)

if (import.meta.env.MODE !== "development") {
  Sentry.init({
    app,
    dsn: "https://60ab22fcc5001893456f1fe80923b2af@o4508802893086720.ingest.us.sentry.io/4508802895052800",
    environment: import.meta.env.MODE, // This will set the environment based on your Node environment
    release: "babble@" + import.meta.env.VITE_APP_VERSION,
    // Enable logs to be sent to Sentry
    enableLogs: true,
    integrations: [
      // Sentry.consoleLoggingIntegration({ levels: ["log", "error"] }),
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
      Sentry.consoleLoggingIntegration({
        levels: ["log", "warn", "error"],
      }),
      Sentry.feedbackIntegration({
        showName: false,
        autoInject: false,
        useSentryUser: {
          email: "email",
          name: "username",
        },
      }),
    ],
    ignoreErrors: [
      // PrimeVue focustrap MutationObserver bug — harmless, just fails to shift focus
      /Cannot read properties of null \(reading 'nextSibling'\)/,
      // Contentless cross-origin / browser-extension noise: Firefox reports a
      // thrown `undefined` via window.onerror with no usable stack frames, and
      // Chrome reports cross-origin failures as "Script error." Neither maps to
      // our code (no bare throws in src/), so there's nothing to act on.
      /uncaught exception: undefined/i,
      "Script error.",
    ],
    // Tracing
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [/^https:\/\/ibabbleon\.com\/dashboard/],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 0.5, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  })
}

app.use(createPinia())
app.use(router)
app.use(PrimeVue, {
  ripple: true,
  license: PRIMEVUE_LICENSE,
  // unstyled: true,
  // pt: Aura,
  // Default theme configuration
  theme: {
    preset: Lara,
    options: {
      prefix: "p",
      darkModeSelector: ".dark",
      cssLayer: false,
    },
  },
})
// app.use(ToastService)
// app.use(DialogService)

app.component("font-awesome-icon", FontAwesomeIcon)
app.component("font-awesome-layers", FontAwesomeLayers)
app.component("font-awesome-layers-text", FontAwesomeLayersText)

app.directive("tooltip", Tooltip)
app.directive("focustrap", FocusTrap)
app.directive("keyfilter", KeyFilter)

app.mount("#app")

// A deploy replaces every hashed asset, so while it uploads, a tab that was
// already open asks for chunks that aren't on disk right now ("Unable to preload
// CSS for …", "error loading dynamically imported module: …"). Pushing "/" can't
// fix that — the new route's chunk is missing too — but a reload picks up the
// fresh index.html and whatever has landed. Guard with sessionStorage so a
// genuinely broken build stops after a couple of tries instead of looping.
const RELOADED_KEY = "babble/chunkreload"
// Deploys are uploaded by hand, so the window where a chunk is missing lasts as
// long as the upload, not an instant. One immediate reload can land inside that
// window and fail again, so allow a second, spaced-out attempt before giving up.
const MAX_RELOADS = 2
const RELOAD_DELAY = 2000

function isChunkLoadError(error: unknown) {
  const message = String((error as Error)?.message || error || "")
  return /Failed to fetch dynamically imported module|error loading dynamically imported module|Unable to preload CSS|Importing a module script failed/i.test(message)
}

// One failed route load raises two errors in the same tick — the CSS preload and
// then the module import — so they have to share a single reload attempt instead
// of burning the whole budget on one incident.
let reloadScheduled = false

function reloadOnceForStaleChunks(error: unknown) {
  if (!isChunkLoadError(error)) return false
  if (reloadScheduled) return true
  const attempts = Number(sessionStorage.getItem(RELOADED_KEY) || 0)
  if (attempts >= MAX_RELOADS) return false
  reloadScheduled = true
  sessionStorage.setItem(RELOADED_KEY, String(attempts + 1))
  setTimeout(() => window.location.reload(), attempts * RELOAD_DELAY)
  return true
}

// Cleared only once a route component has really loaded. app.mount() succeeds even
// when every lazy chunk is missing, so clearing this at module scope — as it used
// to — handed back a full reload budget on every load and let a permanently broken
// chunk reload the tab forever.
router.isReady().then(
  () => sessionStorage.removeItem(RELOADED_KEY),
  () => {}
)

// Vite fires this for preload (JS and CSS) failures, which never reach the router.
window.addEventListener("vite:preloadError", (event) => {
  if (reloadOnceForStaleChunks((event as any)?.payload)) event.preventDefault()
})

// Add error handler for router
let handlingRouterError = false
router.onError((error) => {
  console.error("Router error:", error)
  if (reloadOnceForStaleChunks(error)) return
  // Avoid infinite loop if pushing "/" also fails
  if (!handlingRouterError) {
    handlingRouterError = true
    router.push("/").finally(() => {
      handlingRouterError = false
    })
  }
})
